import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { WorkActivity } from "@torre/work-hours";
import { createActivity, searchActivities, timeEntryErrorMessage, type ActivityRow } from "../services/workHoursService";
import { inputClass } from "../theme";
import type { ThemeClasses } from "../../../types/components";

// ---------------------------------------------------------------------------
// Selector de ticket para «Registrar tiempo» (#1303 / #1306). Toda entrada
// manual va ligada a una actividad: aquí se busca una existente (el sprint
// activo primero, luego el backlog) o se crea una al vuelo con el mismo «Tipo»
// que la entrada. Sin ticket el formulario no guarda.
// ---------------------------------------------------------------------------

const DEBOUNCE_MS = 300;
const STATUS_TONE: Record<string, string> = {
  backlog: "bg-gray-400/15 text-gray-500",
  todo: "bg-sky-500/15 text-sky-600",
  in_progress: "bg-violet-500/15 text-violet-600",
  review: "bg-amber-500/15 text-amber-600",
  blocked: "bg-red-500/15 text-red-500",
  done: "bg-emerald-500/15 text-emerald-600",
};

interface Props {
  isDarkMode: boolean;
  tc: ThemeClasses;
  /** Tipo elegido en el formulario: con él se crea el ticket nuevo. */
  typeKey: string;
  /** Proyecto y repo del formulario, para dejar el ticket ligado a ellos. */
  projectId?: string | null;
  repo?: string | null;
  value: WorkActivity | null;
  onChange: (activity: WorkActivity | null) => void;
  disabled?: boolean;
}

/** Ordena: sprint activo primero, luego lo demás; dentro, por clave. */
function rank(rows: ActivityRow[], activeSprintId: string | null | undefined): ActivityRow[] {
  return [...rows].sort((a, b) => {
    const sa = activeSprintId && a.sprintId === activeSprintId ? 0 : 1;
    const sb = activeSprintId && b.sprintId === activeSprintId ? 0 : 1;
    return sa - sb || String(a.key).localeCompare(String(b.key));
  });
}

export default function ActivityPicker({ isDarkMode, tc, typeKey, projectId, repo, value, onChange, disabled }: Props) {
  const { t } = useTranslation("common");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [activeSprintId, setActiveSprintId] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Búsqueda con espera: una llamada por pausa de escritura, no por tecla. La
  // primera carga (sin texto) trae el sprint activo y el backlog para elegir.
  useEffect(() => {
    if (value || creating) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSearching(true);
      setSearchError(false);
      searchActivities({ q: query.trim() || undefined, limit: 30 })
        .then((res) => {
          setRows(rank(res.data, res.meta.activeSprintId));
          setActiveSprintId(res.meta.activeSprintId ?? null);
        })
        .catch(() => setSearchError(true))
        .finally(() => setSearching(false));
    }, DEBOUNCE_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [query, value, creating]);

  const field = `mt-1 w-full ${inputClass(isDarkMode)}`;
  const visible = useMemo(() => rows.slice(0, 30), [rows]);

  const create = async () => {
    const title = newTitle.trim().replace(/\s+/g, " ");
    if (title.length < 3) return setCreateError(t("workHours.timeEntries.add.errors.activityTitle"));
    setSaving(true);
    setCreateError(null);
    try {
      const res = await createActivity({ title, typeKey, projectId: projectId || null, repo: repo || null });
      onChange(res.data);
      setCreating(false);
      setNewTitle("");
    } catch (err) {
      setCreateError(timeEntryErrorMessage(err) ?? t("workHours.timeEntries.add.errors.activityCreate"));
    } finally {
      setSaving(false);
    }
  };

  if (value) {
    return (
      <div className={`mt-1 flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-xs ${tc.border}`} data-testid="activity-picker-selected">
        <span className="flex min-w-0 items-center gap-2">
          <i className="ri-ticket-line text-violet-500" />
          <span className="font-mono">{value.key}</span>
          <span className={`truncate ${tc.textSecondary}`}>{value.title}</span>
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${STATUS_TONE[value.status] ?? ""}`}>{t(`workHours.timeEntries.activityStatus.${value.status}`, { defaultValue: value.status })}</span>
        </span>
        {!disabled && (
          <button type="button" onClick={() => onChange(null)} className="text-[11px] text-violet-500 hover:underline" data-testid="activity-picker-change">
            {t("workHours.timeEntries.add.changeActivity")}
          </button>
        )}
      </div>
    );
  }

  if (creating) {
    return (
      <div className={`mt-1 space-y-2 rounded-lg border p-3 ${tc.border}`} data-testid="activity-picker-create">
        <p className={`text-[11px] ${tc.textSecondary}`}>{t("workHours.timeEntries.add.createActivityHint")}</p>
        <input
          aria-label={t("workHours.timeEntries.add.activityTitle")}
          value={newTitle}
          maxLength={200}
          placeholder={t("workHours.timeEntries.add.activityTitlePlaceholder")}
          onChange={(e) => setNewTitle(e.target.value)}
          className={`w-full ${inputClass(isDarkMode)}`}
          autoFocus
        />
        {createError && (
          <p className="text-xs text-red-500" data-testid="activity-picker-create-error">
            {createError}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => setCreating(false)} disabled={saving} className={`rounded-lg border px-2 py-1 text-[11px] ${tc.border} ${tc.textSecondary}`}>
            {t("workHours.cancel")}
          </button>
          <button type="button" onClick={() => void create()} disabled={saving} className="rounded-lg bg-violet-600 px-2 py-1 text-[11px] font-medium text-white disabled:opacity-60" data-testid="activity-picker-create-save">
            {saving ? t("workHours.timeEntries.add.saving") : t("workHours.timeEntries.add.createActivityConfirm")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="activity-picker">
      <input
        aria-label={t("workHours.timeEntries.add.searchActivity")}
        value={query}
        placeholder={t("workHours.timeEntries.add.searchActivityPlaceholder")}
        onChange={(e) => setQuery(e.target.value)}
        disabled={disabled}
        className={field}
      />
      <div className={`mt-1 max-h-44 overflow-y-auto rounded-lg border text-xs ${tc.border}`}>
        {/* #1404: «Crear ticket» va hasta arriba; abajo, los tickets que ya existen. */}
        <button
          type="button"
          onClick={() => {
            setNewTitle(query.trim());
            setCreating(true);
          }}
          disabled={disabled}
          className={`sticky top-0 flex w-full items-center gap-2 border-b px-3 py-2 text-left font-medium text-violet-500 ${tc.border} ${tc.cardBg} ${tc.buttonHover}`}
          data-testid="activity-picker-new"
        >
          <i className="ri-add-line" />
          {t("workHours.timeEntries.add.createActivity")}
        </button>
        {searchError && <p className="px-3 py-2 text-red-500">{t("workHours.timeEntries.add.errors.activities")}</p>}
        {!searchError && searching && visible.length === 0 && <p className={`px-3 py-2 ${tc.textSecondary}`}>{t("workHours.timeEntries.add.searching")}</p>}
        {!searchError && !searching && visible.length === 0 && <p className={`px-3 py-2 ${tc.textSecondary}`}>{t("workHours.timeEntries.add.noActivities")}</p>}
        {visible.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => onChange(a)}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${tc.buttonHover}`}
            data-testid={`activity-option-${a.id}`}
          >
            <span className="font-mono">{a.key}</span>
            <span className={`flex-1 truncate ${tc.text}`}>{a.title}</span>
            {activeSprintId && a.sprintId === activeSprintId && <span className="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[10px] text-violet-600">{t("workHours.timeEntries.add.inSprint")}</span>}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${STATUS_TONE[a.status] ?? ""}`}>{t(`workHours.timeEntries.activityStatus.${a.status}`, { defaultValue: a.status })}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
